<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { picoSearch } from '@chatwoot/pico-search';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { BaseTable, BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const searchQuery = ref('');
const showDeleteConfirmationPopup = ref(false);
const selectedFlow = ref({});
const confirmDialog = ref(null);
const activatingId = ref(null);

const records = computed(() => getters['botFlows/getBotFlows'].value);
const uiFlags = computed(() => getters['botFlows/getUIFlags'].value);
const inboxes = computed(() => getters['inboxes/getInboxes'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name']);
});

const inboxName = inboxId => {
  if (!inboxId) return t('BOT_FLOWS.LIST.NO_INBOX');
  return inboxes.value.find(inbox => inbox.id === inboxId)?.name || '—';
};

onMounted(() => {
  store.dispatch('botFlows/get');
  store.dispatch('inboxes/get');
});

const goToNew = () => router.push(accountScopedRoute('bot_flows_new'));
const goToEdit = flow =>
  router.push(accountScopedRoute('bot_flows_edit', { botFlowId: flow.id }));

const activate = async flow => {
  activatingId.value = flow.id;
  try {
    await store.dispatch('botFlows/activate', flow.id);
    useAlert(t('BOT_FLOWS.LIST.ACTIVATE_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('BOT_FLOWS.LIST.ACTIVATE_ERROR'));
  } finally {
    activatingId.value = null;
  }
};

const deactivate = async flow => {
  activatingId.value = flow.id;
  try {
    await store.dispatch('botFlows/update', { id: flow.id, active: false });
  } finally {
    activatingId.value = null;
  }
};

const openDeletePopup = flow => {
  selectedFlow.value = flow;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};
const confirmDeletion = async () => {
  closeDeletePopup();
  try {
    await store.dispatch('botFlows/delete', selectedFlow.value.id);
    useAlert(t('BOT_FLOWS.LIST.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('BOT_FLOWS.LIST.DELETE_ERROR'));
  }
};

const tableHeaders = computed(() => [
  t('BOT_FLOWS.LIST.TABLE_HEADER.NAME'),
  t('BOT_FLOWS.LIST.TABLE_HEADER.INBOX'),
  t('BOT_FLOWS.LIST.TABLE_HEADER.STATUS'),
  t('BOT_FLOWS.LIST.TABLE_HEADER.ACTIONS'),
]);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('BOT_FLOWS.LIST.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('BOT_FLOWS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('BOT_FLOWS.HEADER')"
        :description="$t('BOT_FLOWS.DESCRIPTION')"
        :search-placeholder="$t('BOT_FLOWS.SEARCH_PLACEHOLDER')"
      >
        <template v-if="filteredRecords.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('BOT_FLOWS.LIST.COUNT', { n: filteredRecords.length }) }}
          </span>
        </template>
        <template #actions>
          <Button :label="$t('BOT_FLOWS.HEADER_BTN_TXT')" size="sm" @click="goToNew" />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="$t('BOT_FLOWS.LIST.404')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="flow in items" :key="flow.id">
            <BaseTableCell>
              <button
                type="button"
                class="font-medium text-n-slate-12 hover:underline"
                @click="goToEdit(flow)"
              >
                {{ flow.name }}
              </button>
            </BaseTableCell>
            <BaseTableCell>{{ inboxName(flow.inbox_id) }}</BaseTableCell>
            <BaseTableCell>
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  flow.active
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{ flow.active ? $t('BOT_FLOWS.LIST.ACTIVE') : $t('BOT_FLOWS.LIST.INACTIVE') }}
              </span>
            </BaseTableCell>
            <BaseTableCell>
              <div class="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="faded"
                  color="slate"
                  :label="$t('BOT_FLOWS.LIST.EDIT')"
                  @click="goToEdit(flow)"
                />
                <Button
                  v-if="!flow.active"
                  size="sm"
                  color="teal"
                  :label="$t('BOT_FLOWS.LIST.ACTIVATE')"
                  :is-loading="activatingId === flow.id"
                  @click="activate(flow)"
                />
                <Button
                  v-else
                  size="sm"
                  variant="faded"
                  color="slate"
                  :label="$t('BOT_FLOWS.LIST.DEACTIVATE')"
                  :is-loading="activatingId === flow.id"
                  @click="deactivate(flow)"
                />
                <Button
                  size="sm"
                  variant="faded"
                  color="ruby"
                  icon="i-lucide-trash-2"
                  @click="openDeletePopup(flow)"
                />
              </div>
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('BOT_FLOWS.DELETE.CONFIRM.TITLE')"
      :message="$t('BOT_FLOWS.DELETE.CONFIRM.MESSAGE')"
      :message-value="selectedFlow.name"
      :confirm-text="$t('BOT_FLOWS.DELETE.CONFIRM.YES')"
      :reject-text="$t('BOT_FLOWS.DELETE.CONFIRM.NO')"
    />
    <woot-confirm-modal ref="confirmDialog" />
  </SettingsLayout>
</template>
